import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  formatLocalizedDateTime,
  formatLocalizedTimeShort,
  formatSchedulerLocalizedDateString,
  getCurrentDate,
  Logger,
  SECONDS_PER_365_DAYS,
  SECONDS_PER_HOUR,
  secondsToHours,
  useLocalize,
} from '@saviynt/common';
import {
  Box,
  Button,
  DateAndTimePicker,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import { addDays, addSeconds, isBefore, isSameDay, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import { formatDate } from '../../utilities';
import {
  getEndpointDetailsApi,
  getExtendSessionApi,
  getListRowApi,
  getSessionsAccountScheduleApi,
} from '../../utilities/api/getDataFromApi';
import { MAX_LENGTH_FOR_JUSTIFICATION } from '../ModalPageForms/constants';

import { ACCOUNT_TYPE, EXTEND_MSGS } from './constants';

import './Extend.css';

const TIMES = { interval: 15 };

const msgs = {
  pam: {
    extend: {
      extendSuccessMessage: {
        id: 'pam.extend.success',
        defaultMessage:
          "You've extended your access to {accountName} until {endDate1} on {endDate2}.",
      },
      extendDurationAllowed: {
        id: 'pam.extend.durationAllowed',
        defaultMessage:
          'Adjust your end time. Your session can’t exceed a total of {maxDuration} hours.',
      },
      extendDurationConflicts: {
        id: 'pam.extend.durationConflicts',
        defaultMessage:
          'Adjust your end time. Another session with this asset is scheduled to start at {startDateOfNextBlockedSession1} on {startDateOfNextBlockedSession2}',
      },
      modalTitle: {
        id: 'pam.extend.modalTitle',
        defaultMessage: 'Extend Session',
      },
      startTime: {
        id: 'pam.extend.startTime',
        defaultMessage: 'Start Time',
      },
      endDate: {
        id: 'pam.extend.endDate',
        defaultMessage: 'End Date',
      },
      endTime: {
        id: 'pam.extend.endTime',
        defaultMessage: 'End Time',
      },
      justification: {
        id: 'pam.extend.justification',
        defaultMessage: 'Justification',
      },
      cancel: {
        id: 'pam.common.cancel',
        defaultMessage: 'Cancel',
      },
      justifPlaceholder: {
        id: 'pam.modalPage.justificationDetails.justifPlaceholder',
        defaultMessage: 'Provide a reason for your request',
      },
      extendLabel: {
        id: 'pam.sidePanel.extend',
        defaultMessage: 'Extend',
      },
    },
  },
};

const maxTimeFrameError = 'SESSION.REQUEST.MAXREQUESTED.TIMEEXCEEDED';
const extendBeforeStartError = 'SESSION.EXTEND.BEFORE.START';

function Extend({
  isExtendModalOpen,
  setIsExtendModalOpen,
  row,
  setAlertBannerData,
  setListRowData,
  userName,
  description,
}) {
  const localize = useLocalize();

  // Get filter state from Redux
  const { selectedFilters, searchValue } = useSelector(
    (state) =>
      state.landingPageFilters || { selectedFilters: {}, searchValue: '' }
  );
  const { checkedPlatforms, checkedAssets, checkedRoles } = selectedFilters;

  // JIT
  const isJit = row.pamAccountType?.toUpperCase().includes('JIT');
  let jitMaxTime = 0;

  const isExclusive = row?.exclusiveAccess === true;
  const isNotExclusive =
    row?.exclusiveAccess === undefined || row?.exclusiveAccess === false;
  const pamType = row?.pamType?.toLowerCase();
  const pamPlatformType = row?.pamPlatformType?.toLowerCase();

  // Critical states
  const [isOutOfRange, setIsOutOfRange] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  // DatePicker states
  const [endTimeMin, setEndTimeMin] = useState({ minutes: 0, hours: 0 });
  const [endTimeMax, setEndTimeMax] = useState({ minutes: 59, hours: 23 });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Details
  const [justificationInputValue, setJustificationInputValue] = useState('');
  // API related states
  const [maxEndDateCalculation, setMaxEndDateCalculation] = useState(null);
  const [sessionsAccountSchedule, setSessionsAccountSchedule] = useState(null);
  const [startDateOfNextBlockedSession, setStartDateOfNextBlockedSession] =
    useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isExtendTimeSame, setIsExtendTimeSame] = useState(true);
  const [endpointDetails, setEndpointDetails] = useState(null);

  // Roles
  const isRole = Boolean(row?.roleKey);
  const isSessionRole = (session) => Boolean(session.roleKey);
  const roleMaxTimeFrameHrs = row?.roleMaxTimeFrameHrs;

  // Calculate max time allowed
  const getMaxTime = () => {
    if (isRole) {
      return roleMaxTimeFrameHrs * SECONDS_PER_HOUR;
    }

    if (row?.accountConfig?.maxrequestabletimeinsecs > 0) {
      return row?.accountConfig?.maxrequestabletimeinsecs;
    }

    return SECONDS_PER_365_DAYS;
  };

  const userMaxTime = getMaxTime();

  const closeOnEscapeKeyDown = (e) => {
    if ((e.charCode || e.keyCode) === 27) {
      setIsExtendModalOpen(false);
    }
  };

  const getEarliestSessionAfterCurrent = (filteredCreds) => {
    if (filteredCreds) {
      // Sort by startDate from newest to oldest
      const sortedCreds = filteredCreds.sort(
        (a, b) => new Date(a.startDate) - new Date(b.startDate)
      );

      // Remove the first item (newest session)
      sortedCreds.shift();

      if (sortedCreds.length > 0) {
        setStartDateOfNextBlockedSession(parseISO(sortedCreds[0]?.startDate));
      }
    }
  };

  const getSessions = (formattedStartDate, formattedEndDate) => {
    const sessionParams = [
      isJit ? undefined : row.accountKey,
      row.endpointKey,
      formattedStartDate,
      formattedEndDate,
      isRole ? ['EMERGENCYACCESSROLE'] : undefined,
      isRole ? [row.roleKey] : undefined,
    ];

    getSessionsAccountScheduleApi(...sessionParams)
      .then((response) => {
        if (Array.isArray(response.result.sessions)) {
          const extractedDates = response.result.sessions
            .filter((session) => {
              // For non-exclusive endpoint for remote app we will block if existing session is for same remote app and by same user
              if (!isExclusive && session.remoteAppMetadata?.name) {
                return (
                  session.remoteAppMetadata?.name ===
                    row.remoteAppMetadata?.name && session.username === userName
                );
              }

              // For exclusive endpoint for remote app we will block if existing session is
              // if same remote app then return all sessions
              // if different remote app then return sessions only by other users

              if (isExclusive && session.remoteAppMetadata?.name) {
                return (
                  session.remoteAppMetadata?.name ===
                    row.remoteAppMetadata?.name ||
                  (session.remoteAppMetadata?.name !==
                    row.remoteAppMetadata?.name &&
                    session.username !== userName)
                );
              }

              return true;
            })
            .filter((session) => {
              // Filter Roles Sessions
              if (isRole && isSessionRole(session)) {
                return row.roleKey === session.roleKey;
              }

              // Filter Endpoint Sessions
              if (isJit) {
                return (
                  session.accountConfig?.['Saviynt-Status']?.justInTime ===
                    'true' && session.username === userName
                );
              }

              if (
                pamType === ACCOUNT_TYPE.CREDENTIAL ||
                (pamType === ACCOUNT_TYPE.CREDENTIALLESS && isExclusive)
              ) {
                return true; // Pass all sessions
              }

              return (
                pamType === ACCOUNT_TYPE.CREDENTIALLESS &&
                isNotExclusive &&
                session.username === userName
              );
            })
            .map((session) => ({
              ...(session.accountKey && { accountKey: session.accountKey }),
              ...(session.endpointKey && { endpointKey: session.endpointKey }),
              ...(session.roleKey && { roleKey: session.roleKey }),
              startDate: session.sessionStartDate,
              endDate: session.sessionEndDate,
            }));

          setSessionsAccountSchedule(extractedDates);
        } else {
          Logger.error('Account sessions is not an array:', response);
        }
      })
      .catch((error) => {
        Logger.error('Error fetching sessions:', error);
      });
  };

  useEffect(() => {
    const validDayStart = getCurrentDate();

    const formattedStartDate = formatDate(validDayStart);
    const formattedEndDate = formatDate(addDays(validDayStart, 365));

    if (isJit) {
      getEndpointDetailsApi(row.endpointKey).then((response) => {
        jitMaxTime =
          response.bootstrapConfigModel?.[pamPlatformType]
            .maxCredlessSessionRequestTime;

        setEndpointDetails(response);
        setMaxEndDateCalculation(
          addSeconds(parseISO(row.sessionStartDate), jitMaxTime)
        );
      });
    }

    getSessions(formattedStartDate, formattedEndDate);

    if (!isJit) {
      setMaxEndDateCalculation(
        addSeconds(parseISO(row.sessionStartDate), userMaxTime)
      );
    }

    setStartDate(parseISO(row.sessionStartDate));
    setEndDate(parseISO(row.sessionEndDate));
    document.body.addEventListener('keydown', closeOnEscapeKeyDown);

    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
  }, []);

  useEffect(() => {
    if (sessionsAccountSchedule?.length > 0) {
      const filteredExpiredCredentials = sessionsAccountSchedule.filter(
        (session) => {
          const sessionEndDate = parseISO(session.endDate);

          return isBefore(startDate, sessionEndDate);
        }
      );

      getEarliestSessionAfterCurrent(filteredExpiredCredentials);
    }
  }, [sessionsAccountSchedule]);

  useEffect(() => {
    setEndTimeMin({ minutes: 0, hours: 0 });
    setEndTimeMax({ minutes: 59, hours: 23 });

    if (startDate && endDate) {
      if (isSameDay(startDate, endDate)) {
        setEndTimeMin({
          hours: parseISO(row.sessionEndDate).getHours(),
          minutes: parseISO(row.sessionEndDate).getMinutes(),
        });
      }

      if (isSameDay(maxEndDateCalculation, endDate)) {
        setEndTimeMax({
          hours: maxEndDateCalculation.getHours(),
          minutes: maxEndDateCalculation.getMinutes(),
        });
      }

      if (
        startDateOfNextBlockedSession &&
        isSameDay(startDateOfNextBlockedSession, endDate) &&
        maxEndDateCalculation !== startDateOfNextBlockedSession
      )
        setStartDateOfNextBlockedSession(startDateOfNextBlockedSession);
    }
  }, [
    endDate,
    startDate,
    startDateOfNextBlockedSession,
    maxEndDateCalculation,
  ]);

  useEffect(() => {
    const isNowInUserRange =
      maxEndDateCalculation && endDate > maxEndDateCalculation;
    const isBlockedBySession =
      startDateOfNextBlockedSession && endDate > startDateOfNextBlockedSession;

    setIsExtendTimeSame(endDate <= parseISO(row.sessionEndDate));

    const extendDurationAllowedMessage = (maxDurationValue) => {
      const maxDuration = secondsToHours(
        parseInt(
          isJit
            ? endpointDetails.bootstrapConfigModel?.[pamPlatformType]
                .maxCredlessSessionRequestTime
            : maxDurationValue,
          10
        )
      );

      return localize(msgs.pam.extend.extendDurationAllowed, { maxDuration });
    };

    if (isNowInUserRange || isBlockedBySession) {
      setIsOutOfRange(true);
      setErrorMessage(
        isNowInUserRange
          ? extendDurationAllowedMessage(isJit ? jitMaxTime : userMaxTime)
          : localize(msgs.pam.extend.extendDurationConflicts, {
              startDateOfNextBlockedSession1: formatLocalizedTimeShort(
                startDateOfNextBlockedSession
              ),
              startDateOfNextBlockedSession2:
                formatSchedulerLocalizedDateString(
                  startDateOfNextBlockedSession
                ),
            })
      );
    } else {
      setIsOutOfRange(false);
    }
  }, [endDate]);

  const filterTimesPassedNextSession = (time) => {
    const maxDate = new Date(startDateOfNextBlockedSession);
    const selectedDate = new Date(time);

    return selectedDate.getTime() < maxDate.getTime();
  };

  // TODO: update this to disable id the endDate is the same as the row.sessionEndDate
  const isExtendDisabled = Boolean(
    !endDate || isCritical || isOutOfRange || isExtendTimeSame
  );
  // Subheader/Modal Description
  const getExtendModalSubHeaderContent = () => (
    <Box className='Extend-description'>
      <Typography kind='body2'>{description}</Typography>
    </Box>
  );

  const parseErrorForMessage = (errorBody) => {
    // Parse for Maximum Time Frame error
    if (errorBody.error === maxTimeFrameError && errorBody.data?.length) {
      return `${localize(errorBody.error)} ${Math.floor(
        secondsToHours(errorBody.data[0])
      )} hours`;
    }

    if (errorBody.error === extendBeforeStartError) {
      return localize(EXTEND_MSGS.extendBeforeStartErrorMessage);
    }

    return localize(errorBody.error || EXTEND_MSGS.extendFailedDefaultMessage);
  };

  const handleJustificationChange = (value) => {
    setJustificationInputValue(value);

    const isJustificationReachedMaxLength =
      value?.trim().length > MAX_LENGTH_FOR_JUSTIFICATION;

    if (isJustificationReachedMaxLength) {
      setIsCritical(true);
    } else {
      setIsCritical(false);
    }
  };

  // Modal Content
  const getExtendModalIslandContent = () => (
    <Box tag='div' className='Extend-content'>
      <div className='Extend-timePicker'>
        <div className='Extend-startTime'>
          <Typography className='Extend-startTime-label' kind='h4'>
            {localize(msgs.pam.extend.startTime)}
          </Typography>

          <Typography kind='body2' className='Extend-startTime-value'>
            {formatLocalizedDateTime(row.sessionStartDate, true, true)}
          </Typography>
        </div>
        <div className='Extend-endDateContainer'>
          {maxEndDateCalculation && sessionsAccountSchedule ? (
            <div className='Extend-endDate'>
              <DateAndTimePicker
                kind='dateEnd'
                label={localize(msgs.pam.extend.endDate)}
                labelTypography='h4'
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                maxDate={maxEndDateCalculation}
                minDate={parseISO(row.sessionEndDate)}
                isCritical={isOutOfRange}
              />
              <DateAndTimePicker
                kind='timeEnd'
                label={localize(msgs.pam.extend.endTime)}
                labelTypography='h4'
                endDate={endDate}
                setEndDate={setEndDate}
                timeIntervals={TIMES.interval}
                dateFormat='h:mm aa'
                timeHeaderText=''
                timeMin={endTimeMin}
                timeMax={endTimeMax}
                filterTime={
                  startDateOfNextBlockedSession
                    ? filterTimesPassedNextSession
                    : null
                }
                isCritical={isOutOfRange}
              />
            </div>
          ) : (
            <div>
              <Box tag='div' className='Extend-loader'>
                <Loader
                  kind='dots'
                  format='inline'
                  color='info'
                  size='medium'
                />
              </Box>
            </div>
          )}
          <InlineMessage
            text={errorMessage}
            colorTheme='critical'
            size='small'
            leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
            isVisible={isOutOfRange}
            className='Pam-Extend-dateErrorMessage'
          />
        </div>

        <InputField
          name='Justification Details'
          placeholder={localize(msgs.pam.extend.justifPlaceholder)}
          label={
            <Typography kind='h4' htmlFor='Justification'>
              {localize(msgs.pam.extend.justification)}
            </Typography>
          }
          value={justificationInputValue}
          setValue={handleJustificationChange}
          kind='multiline'
          isCritical={isCritical}
          isRequired
          minRequiredChars={3}
          CriticalHelperText={
            justificationInputValue?.trim().length >
            MAX_LENGTH_FOR_JUSTIFICATION ? (
              <InlineMessage
                text={`Max character limit reached ${
                  justificationInputValue?.trim().length
                }/${MAX_LENGTH_FOR_JUSTIFICATION}`}
                colorTheme='critical'
                size='medium'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            ) : (
              <InlineMessage kind='missingRequiredField' size='small' />
            )
          }
        />
      </div>
    </Box>
  );

  // Modal Footer
  const getExtendModalFooterContent = () => (
    <FooterActionBar className='Extend-footer'>
      <div className='Extend-footer-buttons'>
        <Button
          type='button'
          kind='outlined'
          size='medium'
          onClick={() => setIsExtendModalOpen(false)}>
          {localize(msgs.pam.extend.cancel)}
        </Button>
        <Button
          type='button'
          kind='filled'
          size='medium'
          isDisabled={isExtendDisabled}
          onClick={() => {
            if (
              justificationInputValue?.trim().length < 3 ||
              justificationInputValue?.trim().length >
                MAX_LENGTH_FOR_JUSTIFICATION
            ) {
              setIsCritical(true);

              return;
            }

            // isoString example: 2024-04-22T04:30:00.000Z
            const isoString = endDate.toISOString();
            const formattedDate = isoString.substring(0, 10);
            const formattedTime = isoString.substring(11, 19);

            // formattedDateTime example: 2024-04-22 04:30:00
            const formattedDateTime = `${formattedDate} ${formattedTime}`;

            getExtendSessionApi(
              row.requestAccessKey,
              formattedDateTime,
              justificationInputValue
            )
              .then((response) => {
                if (response.success) {
                  setAlertBannerData((prevState) => [
                    ...prevState,
                    {
                      colorTheme: 'Success',
                      title: localize(EXTEND_MSGS.alertBannerSuccessTitle),
                      description: localize(
                        msgs.pam.extend.extendSuccessMessage,
                        {
                          accountName: row?.accountName,
                          endDate1: formatLocalizedTimeShort(endDate),
                          endDate2: formatSchedulerLocalizedDateString(endDate),
                        }
                      ),
                    },
                  ]);
                } else {
                  response.json().then((errorBody) => {
                    setAlertBannerData((prevState) => [
                      ...prevState,
                      {
                        colorTheme: 'Critical',
                        title: localize(EXTEND_MSGS.alertBannerFailureTitle),
                        description: parseErrorForMessage(errorBody),
                      },
                    ]);
                  });
                }

                getListRowApi({
                  checkedPlatforms,
                  checkedAssets,
                  checkedRoles,
                  searchValue,
                }).then((res) => {
                  setListRowData(res);
                });
              })
              .catch((error) => {
                Logger.error('Error fetching sessions:', error);
              });

            setIsExtendModalOpen(false);
          }}>
          {localize(msgs.pam.extend.extendLabel)}
        </Button>
      </div>
    </FooterActionBar>
  );

  return (
    <ModalIsland
      className='Extend'
      onClose={() => {
        setIsExtendModalOpen(false);
      }}
      isOpen={isExtendModalOpen}
      title={localize(msgs.pam.extend.modalTitle)}
      subtitle={description && getExtendModalSubHeaderContent()}
      contentBody={getExtendModalIslandContent()}
      FooterActionBarComp={getExtendModalFooterContent()}
    />
  );
}

Extend.propTypes = {
  row: PropTypes.shape({
    sessionStartDate: PropTypes.string,
    sessionEndDate: PropTypes.string,
    accountKey: PropTypes.number,
    pamType: PropTypes.string,
    endpointKey: PropTypes.number,
    pamPlatformType: PropTypes.string,
    pamAccountType: PropTypes.string,
    exclusiveAccess: PropTypes.bool,
    roleKey: PropTypes.string,
    roleMaxTimeFrameHrs: PropTypes.number,
    roleName: PropTypes.string,
  }).isRequired,
  isExtendModalOpen: PropTypes.bool.isRequired,
  setIsExtendModalOpen: PropTypes.func.isRequired,
  setAlertBannerData: PropTypes.func.isRequired,
  setListRowData: PropTypes.func.isRequired,
  userName: PropTypes.string.isRequired,
  description: PropTypes.string,
};

Extend.defaultProps = { description: null };
export default Extend;
